import React from "react";
import { API } from "../API";
import { isLoading, Loading, loading } from "../Loading";
import {
  NursingQuestionId,
  NursingQuestionVBPAnswer,
  NursingQuestionVBPReportSubmit,
  NursingQuestionVBPWithAnswer,
  VisitInstanceId,
} from "../messages";
import { withDefault } from "../utils/withDefault";
import { isDefined } from "./Generic/IsDefined";

interface Props {
  api: API;
  visitInstanceId: VisitInstanceId;
}

export default function useVbpNursingQuestions(props: Props) {
  const [questions, setQuestions] = React.useState<Loading | NursingQuestionVBPWithAnswer[]>(
    loading()
  );

  const changeQuestion = (changedQuestion: NursingQuestionVBPWithAnswer) => {
    if (isLoading(changedQuestion)) {
      return;
    }

    setQuestions((prevQuestions) => {
      if (isLoading(prevQuestions)) {
        return loading();
      }

      return prevQuestions.map((prevQuestion) => {
        if (prevQuestion.id === changedQuestion.id) {
          return changedQuestion;
        }

        return prevQuestion;
      });
    });
  };

  const answerVbpQuestion = (vbpAnswer: NursingQuestionVBPAnswer): Promise<void> => {
    return props.api.submitVBPNursingQuestionAnswerForVisit(props.visitInstanceId, vbpAnswer);
  };

  const submitReport = (vbpReportSubmit: NursingQuestionVBPReportSubmit): Promise<void> => {
    return props.api.submitVBPNursingQuestionsForVisit(props.visitInstanceId, vbpReportSubmit);
  };

  React.useEffect(() => {
    props.api
      .getVBPNursingQuestionsForVisit(props.visitInstanceId)
      .then((data) => data.questions.map(mapQuestionFromServer))
      .then(setQuestions);
  }, [props.api, props.visitInstanceId]);

  return {
    vbpNursingQuestions: questions,
    changeVbpNursingQuestion: changeQuestion,
    answerVbpNursingQuestion: answerVbpQuestion,
    submitVbpNursingReport: submitReport,
  };
}

const DEFAULT_RISK_LEVEL = 1;

function initialAnswerDetails(
  nursingQuestionId: NursingQuestionId,
  defaultRiskLevel: number
): NursingQuestionVBPAnswer {
  return {
    nursingQuestionId: nursingQuestionId,
    notes: "",
    riskLevel: defaultRiskLevel,
    answer: "",
  };
}

function mapQuestionFromServer(
  question: NursingQuestionVBPWithAnswer
): NursingQuestionVBPWithAnswer {
  return {
    ...question,
    answerDetails: isDefined(question.answerDetails)
      ? {
          ...question.answerDetails,
          riskLevel: withDefault(question.answerDetails.riskLevel, DEFAULT_RISK_LEVEL),
        }
      : initialAnswerDetails(question.id, DEFAULT_RISK_LEVEL),
  };
}
